import { useState, useEffect } from "react";
import {
  collection, query, where, getDocs, addDoc, doc, updateDoc, writeBatch
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { getFlagEmoji } from "../utils/flagEmoji";

function Avatar({ name, photo, size = 44 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: "#1E2D4A", display: "flex", alignItems: "center",
      justifyContent: "center", fontSize: size * 0.38, fontWeight: 500,
      color: "white", flexShrink: 0, overflow: "hidden",
    }}>
      {photo
        ? <img src={photo} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}} />
        : (name?.charAt(0)?.toUpperCase() || "?")}
    </div>
  );
}

export default function Friends({ profile, trips, copyInviteLink, copiedTripId }) {
  const [tab, setTab] = useState("friends");
  const [friends, setFriends] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [friendUids, setFriendUids] = useState(new Set());
  const [sentTo, setSentTo] = useState(new Set());
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchDone, setSearchDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [inviteFor, setInviteFor] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    if (!auth.currentUser) {
      setLoading(false);
      return;
    }
    try {
      const uid = auth.currentUser.uid;

      const friendsSnap = await getDocs(
        query(collection(db, "friends"), where("uid", "==", uid))
      );
      const friendsList = friendsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setFriends(friendsList);
      setFriendUids(new Set(friendsList.map(f => f.friendUid)));

      const reqSnap = await getDocs(
        query(collection(db, "friendRequests"), where("toUid", "==", uid))
      );
      setIncoming(
        reqSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(r => r.status === "pending")
      );

      const sentSnap = await getDocs(
        query(collection(db, "friendRequests"), where("fromUid", "==", uid))
      );
      setSentTo(new Set(
        sentSnap.docs.map(d => d.data()).filter(r => r.status === "pending").map(r => r.toUid)
      ));
    } catch (err) {
      console.error("Friends.fetchData failed:", err);
      setError("Failed to load. Please try again.");
    }
    setLoading(false);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQ.trim()) return;
    setSearching(true);
    setSearchDone(false);
    try {
      const snap = await getDocs(
        query(collection(db, "users"), where("username", "==", searchQ.trim().toLowerCase()))
      );
      setSearchResults(
        snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(u => u.id !== auth.currentUser?.uid)
      );
      setSearchDone(true);
    } catch (err) {
      console.error("Friends.handleSearch failed:", err);
      setSearchResults([]);
      setSearchDone(true);
      setError("Search failed. Please try again.");
    }
    setSearching(false);
  };

  const sendRequest = async (toUser) => {
    setError(null);
    if (!auth.currentUser) return;
    try {
      await addDoc(collection(db, "friendRequests"), {
        fromUid: auth.currentUser.uid,
        toUid: toUser.id,
        fromName: profile?.name || "",
        fromPhoto: profile?.photoURL || null,
        fromVibe: profile?.vibeLabel || "",
        fromVibeEmoji: profile?.vibeEmoji || "",
        fromUsername: profile?.username || "",
        status: "pending",
        createdAt: new Date(),
      });
      setSentTo(prev => new Set([...prev, toUser.id]));
    } catch (err) {
      console.error("Friends.sendRequest failed:", err);
      setError("Failed to send request. Please try again.");
    }
  };

  const acceptRequest = async (req) => {
    setError(null);
    if (!auth.currentUser) return;
    try {
      const uid = auth.currentUser.uid;
      const batch = writeBatch(db);

      batch.update(doc(db, "friendRequests", req.id), { status: "accepted" });

      const ref1 = doc(collection(db, "friends"));
      batch.set(ref1, {
        uid, friendUid: req.fromUid,
        friendName: req.fromName || "", friendPhoto: req.fromPhoto || null,
        friendVibe: req.fromVibe || "", friendVibeEmoji: req.fromVibeEmoji || "",
        friendUsername: req.fromUsername || "", createdAt: new Date(),
      });

      const ref2 = doc(collection(db, "friends"));
      batch.set(ref2, {
        uid: req.fromUid, friendUid: uid,
        friendName: profile?.name || "", friendPhoto: profile?.photoURL || null,
        friendVibe: profile?.vibeLabel || "", friendVibeEmoji: profile?.vibeEmoji || "",
        friendUsername: profile?.username || "", createdAt: new Date(),
      });

      await batch.commit();
      fetchData();
    } catch (err) {
      console.error("Friends.acceptRequest failed:", err);
      setError("Failed to accept request. Please try again.");
    }
  };

  const declineRequest = async (req) => {
    setError(null);
    try {
      await updateDoc(doc(db, "friendRequests", req.id), { status: "declined" });
      setIncoming(prev => prev.filter(r => r.id !== req.id));
    } catch (err) {
      console.error("Friends.declineRequest failed:", err);
      setError("Failed to decline request. Please try again.");
    }
  };

  return (
    <>
      <style>{`
        .fr-tabs { display: flex; gap: 8px; margin-bottom: 16px; flex-wrap: wrap; }
        .fr-tab { position: relative; padding: 7px 16px; border-radius: 100px; font-size: 13px; font-weight: 500; border: 1.5px solid rgba(26,23,20,0.12); background: white; color: rgba(26,23,20,0.5); cursor: pointer; font-family: 'DM Sans', sans-serif; transition: all 0.15s; }
        .fr-tab.active { background: #1E2D4A; color: white; border-color: #1E2D4A; }
        .fr-badge { position: absolute; top: -6px; right: -6px; min-width: 17px; height: 17px; padding: 0 4px; border-radius: 100px; background: #F5A623; font-size: 9px; font-weight: 700; color: #1E2D4A; display: flex; align-items: center; justify-content: center; border: 2px solid #FAF6F0; }
        .fr-search-form { display: flex; gap: 8px; margin-bottom: 16px; }
        .fr-search-input { flex: 1; padding: 11px 14px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 12px; background: white; font-size: 14px; font-family: 'DM Sans', sans-serif; color: #1a1714; outline: none; }
        .fr-search-input:focus { border-color: #F5A623; }
        .fr-search-btn { padding: 11px 18px; background: #1E2D4A; color: white; border: none; border-radius: 12px; font-size: 13px; font-weight: 500; cursor: pointer; font-family: 'DM Sans', sans-serif; white-space: nowrap; }
        .fr-search-hint { font-size: 12px; color: rgba(26,23,20,0.4); margin-bottom: 14px; }
        .fr-card { background: white; border-radius: 14px; border: 0.5px solid rgba(26,23,20,0.08); padding: 14px; margin-bottom: 10px; display: flex; align-items: center; gap: 12px; }
        .fr-info { flex: 1; min-width: 0; }
        .fr-name { font-size: 14px; font-weight: 500; color: #1a1714; margin-bottom: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .fr-uname { font-size: 11px; color: rgba(26,23,20,0.4); }
        .fr-vibe { font-size: 11px; color: #F5A623; margin-top: 2px; }
        .fr-actions { display: flex; gap: 6px; flex-shrink: 0; flex-wrap: wrap; justify-content: flex-end; }
        .fr-btn { padding: 6px 13px; border-radius: 100px; font-size: 12px; font-weight: 500; cursor: pointer; font-family: 'DM Sans', sans-serif; border: 1.5px solid rgba(26,23,20,0.12); background: white; color: rgba(26,23,20,0.55); transition: all 0.15s; white-space: nowrap; }
        .fr-btn:hover { border-color: #F5A623; color: #C47A0A; }
        .fr-btn-navy { background: #1E2D4A; color: white; border-color: #1E2D4A; }
        .fr-btn-navy:hover { background: #2C3E5E; border-color: #2C3E5E; color: white; }
        .fr-btn-green { background: #1D9E75; color: white; border-color: #1D9E75; }
        .fr-btn-green:hover { background: #188a64; border-color: #188a64; color: white; }
        .fr-btn-red { color: #c9622f; border-color: rgba(201,98,47,0.25); }
        .fr-btn-red:hover { background: #fef0ed; }
        .fr-btn-muted { opacity: 0.55; cursor: default !important; }
        .fr-btn-muted:hover { border-color: rgba(26,23,20,0.12); color: rgba(26,23,20,0.55); }
        .fr-empty { text-align: center; padding: 40px 20px; background: white; border-radius: 16px; border: 1.5px dashed rgba(26,23,20,0.12); }
        .fr-empty-icon { font-size: 34px; margin-bottom: 10px; }
        .fr-empty-title { font-size: 15px; font-weight: 500; color: #1a1714; margin-bottom: 6px; }
        .fr-empty-sub { font-size: 13px; color: rgba(26,23,20,0.45); line-height: 1.55; }
        .fr-my-username { background: white; border-radius: 14px; border: 0.5px solid rgba(26,23,20,0.08); padding: 14px 16px; margin-bottom: 16px; display: flex; align-items: center; gap: 10px; }
        .fr-my-username-label { font-size: 11px; color: rgba(26,23,20,0.4); margin-bottom: 2px; text-transform: uppercase; letter-spacing: 0.05em; }
        .fr-my-username-val { font-size: 15px; font-weight: 500; color: #1a1714; }
        .fr-sheet-overlay { position: fixed; inset: 0; background: rgba(26,23,20,0.5); z-index: 300; display: flex; align-items: flex-end; justify-content: center; }
        .fr-sheet { background: white; border-radius: 20px 20px 0 0; padding: 24px; width: 100%; max-width: 600px; max-height: 75vh; overflow-y: auto; animation: frSlideUp 0.3s ease; }
        @keyframes frSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .fr-sheet-title { font-family: 'Playfair Display', serif; font-size: 18px; font-weight: 600; color: #1a1714; margin-bottom: 16px; }
        .fr-trip-row { display: flex; align-items: center; justify-content: space-between; padding: 12px 0; border-top: 0.5px solid rgba(26,23,20,0.08); gap: 12px; }
        .fr-trip-name { font-size: 14px; font-weight: 500; color: #1a1714; margin-bottom: 2px; }
        .fr-trip-dest { font-size: 12px; color: rgba(26,23,20,0.45); }
        .fr-close-btn { width: 100%; margin-top: 16px; padding: 12px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 12px; background: white; font-size: 14px; font-weight: 500; color: rgba(26,23,20,0.5); cursor: pointer; font-family: 'DM Sans', sans-serif; }
      `}</style>

      {profile?.username ? (
        <div className="fr-my-username">
          <div style={{flex:1}}>
            <div className="fr-my-username-label">Your username</div>
            <div className="fr-my-username-val">@{profile.username}</div>
          </div>
          <span style={{fontSize:11,color:'rgba(26,23,20,0.35)'}}>Share so friends can find you</span>
        </div>
      ) : (
        <div className="fr-my-username">
          <div style={{flex:1}}>
            <div className="fr-my-username-label">No username set</div>
            <div style={{fontSize:12,color:'rgba(26,23,20,0.45)'}}>Add a username in Edit Profile so friends can find you.</div>
          </div>
        </div>
      )}

      <div className="fr-tabs">
        <button className={`fr-tab ${tab === "friends" ? "active" : ""}`} onClick={() => setTab("friends")}>
          Friends {!loading && friends.length > 0 && `(${friends.length})`}
        </button>
        <button className={`fr-tab ${tab === "requests" ? "active" : ""}`} onClick={() => setTab("requests")}>
          Requests
          {incoming.length > 0 && <span className="fr-badge">{incoming.length}</span>}
        </button>
        <button className={`fr-tab ${tab === "search" ? "active" : ""}`} onClick={() => setTab("search")}>
          Find Friends
        </button>
      </div>

      {error && (
        <p style={{fontSize:13,color:'#c9622f',marginBottom:12}}>{error}</p>
      )}

      {tab === "search" && (
        <>
          <p className="fr-search-hint">Search by exact username to find someone.</p>
          <form className="fr-search-form" onSubmit={handleSearch}>
            <input
              className="fr-search-input"
              placeholder="Enter username..."
              value={searchQ}
              onChange={e => setSearchQ(e.target.value.toLowerCase().replace(/\s/g, ""))}
            />
            <button type="submit" className="fr-search-btn" disabled={searching}>
              {searching ? "..." : "Search"}
            </button>
          </form>
          {searchDone && searchResults.length === 0 && (
            <div className="fr-empty">
              <div className="fr-empty-icon">🔍</div>
              <div className="fr-empty-title">No user found</div>
              <div className="fr-empty-sub">Double-check the username. They may not have set one up yet.</div>
            </div>
          )}
          {searchResults.map(user => (
            <div key={user.id} className="fr-card">
              <Avatar name={user.name} photo={user.photoURL} />
              <div className="fr-info">
                <div className="fr-name">{user.name}</div>
                <div className="fr-uname">@{user.username}</div>
                {user.vibeLabel && <div className="fr-vibe">{user.vibeEmoji} {user.vibeLabel}</div>}
              </div>
              <div className="fr-actions">
                {friendUids.has(user.id) ? (
                  <span className="fr-btn fr-btn-green fr-btn-muted">✓ Friends</span>
                ) : sentTo.has(user.id) ? (
                  <span className="fr-btn fr-btn-muted">Sent ✓</span>
                ) : (
                  <button className="fr-btn fr-btn-navy" onClick={() => sendRequest(user)}>+ Add</button>
                )}
              </div>
            </div>
          ))}
        </>
      )}

      {tab === "requests" && (
        <>
          {loading ? (
            <p style={{textAlign:"center",color:"rgba(26,23,20,0.4)",fontSize:14}}>Loading...</p>
          ) : incoming.length === 0 ? (
            <div className="fr-empty">
              <div className="fr-empty-icon">📬</div>
              <div className="fr-empty-title">No pending requests</div>
              <div className="fr-empty-sub">Friend requests you receive will appear here.</div>
            </div>
          ) : incoming.map(req => (
            <div key={req.id} className="fr-card">
              <Avatar name={req.fromName} photo={req.fromPhoto} />
              <div className="fr-info">
                <div className="fr-name">{req.fromName}</div>
                {req.fromUsername && <div className="fr-uname">@{req.fromUsername}</div>}
                {req.fromVibe && <div className="fr-vibe">{req.fromVibeEmoji} {req.fromVibe}</div>}
              </div>
              <div className="fr-actions">
                <button className="fr-btn fr-btn-green" onClick={() => acceptRequest(req)}>✓ Accept</button>
                <button className="fr-btn fr-btn-red" onClick={() => declineRequest(req)}>✕</button>
              </div>
            </div>
          ))}
        </>
      )}

      {tab === "friends" && (
        <>
          {loading ? (
            <p style={{textAlign:"center",color:"rgba(26,23,20,0.4)",fontSize:14}}>Loading...</p>
          ) : friends.length === 0 ? (
            <div className="fr-empty">
              <div className="fr-empty-icon">👥</div>
              <div className="fr-empty-title">No friends yet</div>
              <div className="fr-empty-sub">Use Find Friends to search by username, or share your username so others can add you.</div>
            </div>
          ) : friends.map(friend => (
            <div key={friend.id} className="fr-card">
              <Avatar name={friend.friendName} photo={friend.friendPhoto} />
              <div className="fr-info">
                <div className="fr-name">{friend.friendName}</div>
                {friend.friendUsername && <div className="fr-uname">@{friend.friendUsername}</div>}
                {friend.friendVibe && <div className="fr-vibe">{friend.friendVibeEmoji} {friend.friendVibe}</div>}
              </div>
              <div className="fr-actions">
                <button className="fr-btn" onClick={() => setInviteFor(friend)}>✈️ Invite</button>
              </div>
            </div>
          ))}
        </>
      )}

      {inviteFor && (
        <div className="fr-sheet-overlay" onClick={e => e.target === e.currentTarget && setInviteFor(null)}>
          <div className="fr-sheet">
            <div className="fr-sheet-title">
              Invite {inviteFor.friendName?.split(" ")[0]} to a trip ✈️
            </div>
            {trips.length === 0 ? (
              <p style={{fontSize:13,color:"rgba(26,23,20,0.45)"}}>You don't have any trips yet.</p>
            ) : trips.map(trip => (
              <div key={trip.id} className="fr-trip-row">
                <div style={{flex:1,minWidth:0}}>
                  <div className="fr-trip-name" style={{whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{trip.name}</div>
                  <div className="fr-trip-dest">{getFlagEmoji(trip.countryCode) || '📍'} {trip.destination}, {trip.country}</div>
                </div>
                <button
                  className={`fr-btn ${copiedTripId === trip.id ? "fr-btn-green" : "fr-btn-navy"}`}
                  onClick={() => copyInviteLink(trip)}
                  style={{flexShrink:0}}
                >
                  {copiedTripId === trip.id ? "✓ Copied!" : "Copy link"}
                </button>
              </div>
            ))}
            <button className="fr-close-btn" onClick={() => setInviteFor(null)}>Close</button>
          </div>
        </div>
      )}
    </>
  );
}
